import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as ImportService from "../services/ImportService.mjs";
import * as FileService from "../services/FileService.mjs";
import { clearSnapshot } from "../utils/importPersist";
import { createRateTracker, pushSample } from "../utils/uploadStats";
import { reportError, notify } from "../notifications/errorBus";

/**
 * UploadManagerContext — the app-wide home for upload EXECUTION.
 *
 * Both the bulk Import wizard and the quick Upload modal hand their work to this
 * provider and then get out of the way: because the engine lives here (mounted
 * above the router in Root.js) the transfer keeps running when the modal is
 * minimised, the user navigates to another page, or opens a second upload. Each
 * batch is a "session"; the floating UploadTray and UploadProgressPanel render
 * from the live session list.
 *
 * The import engine is a faithful port of the logic that used to live inside
 * ImportWizardModal (wave upload → chunked create → job polling → failure
 * classification). The classification in `finalizeRun` is correctness-critical:
 * it decides HOW each failed file is retried — by re-uploading (its bytes never
 * reached GCS) or by re-importing the SAME fileId. The backend finalize is
 * idempotent on fileId (a file a crashed/duplicate run already registered
 * surfaces FILE_ALREADY_EXISTS, treated as success), so re-importing a file that
 * already landed is a no-op and can NEVER duplicate it — which is what makes a
 * crashed job's files safely retryable. See the comments there.
 */

// ── Import tuning (unchanged from the original wizard) ──────────────────────
const WAVE_SIZE = 25; // files per batch-init wave (signed URLs expire in 15 min)
const UPLOAD_CONCURRENCY = 6; // simultaneous direct-to-GCS PUTs (import)
const CREATE_CHUNK = 1000; // must stay ≤ backend MAX_IMPORT_FILES
const POLL_MS = 1500;
const MAX_POLL_FAILS = 6; // consecutive failed poll ticks before giving up
// No finalize progress for this long while the server is STILL answering polls →
// the background worker is almost certainly dead (e.g. OOM-killed, which can't
// flip the job to "failed" itself). Surface it instead of polling a frozen job
// forever. Generous so a slow-but-alive large-file batch (each file streams a
// full server-side hash) isn't mistaken for a stall.
const STALL_TIMEOUT_MS = 3 * 60 * 1000;

// ── Quick-upload tuning ─────────────────────────────────────────────────────
const QUICK_CONCURRENCY = 5;

const SAMPLE_MS = 350; // throughput sampler cadence
const SAMPLE_CAP = 64; // points kept for the sparkline

const SILENT = { skipGlobalErrorToast: true }; // one summary, not N toasts

const UploadManagerContext = createContext(null);

/** Run `fn` over `items` with at most `limit` concurrent (ported from wizard). */
async function runPool(items, limit, fn) {
    let i = 0;
    const worker = async () => {
        while (i < items.length) {
            const idx = i++;
            await fn(items[idx], idx);
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

const isActivePhase = (phase) => phase === "uploading" || phase === "finalizing";

export function UploadManagerProvider({ children }) {
    // Live, mutable sessions keyed by id (the engine writes here at high
    // frequency); `sessions` is the throttled render snapshot.
    const sessionsRef = useRef(new Map());
    const enginesRef = useRef(new Map()); // id -> non-serialisable engine bookkeeping
    const [sessions, setSessions] = useState([]);
    const [presentedIds, setPresentedIds] = useState(() => new Set()); // shown in a modal → hidden from tray
    const [trayOpen, setTrayOpen] = useState(true);

    const samplerRef = useRef(null);

    // ── snapshot / flush ─────────────────────────────────────────────────────
    const flush = useCallback(() => {
        // Every mutable nested field (samples/activeTransfers/failures/warnings/
        // uploadedFiles/result) is reassigned wholesale by the engine — never
        // mutated in place — so a shallow session clone is enough to give React a
        // fresh identity without copying potentially-large arrays on every tick.
        const snap = [...sessionsRef.current.values()]
            .map((s) => ({ ...s }))
            .sort((a, b) => b.createdAt - a.createdAt);
        setSessions(snap);
    }, []);

    // ── throughput sampler (single interval for all active sessions) ──────────
    const tick = useCallback(() => {
        const now = Date.now();
        let anyActive = false;
        for (const [id, s] of sessionsRef.current) {
            const e = enginesRef.current.get(id);
            if (!e) continue;
            if (s.phase === "uploading") {
                anyActive = true;
                const activeArr = [...e.active.values()].map((t) => ({
                    ...t,
                    pct: t.size ? Math.min(100, Math.round((t.loaded * 100) / t.size)) : 0,
                }));
                const inflight = activeArr.reduce((sum, t) => sum + t.loaded, 0);
                const liveLoaded = e.bytesCompleted + inflight;
                const rate = e.rateTracker.update(liveLoaded, now);
                s.bytesLoaded = liveLoaded;
                s.bytesFailed = e.bytesFailed;
                s.filesUploaded = e.filesUploaded;
                s.filesFailed = e.filesFailed;
                s.filesDone = e.filesUploaded + e.filesFailed;
                s.speed = rate;
                s.rateUnit = "bytes";
                // failed bytes are "accounted" (they won't transfer), so the bar
                // can still reach 100%; ETA only counts bytes that can still move.
                s.etaMs = e.rateTracker.etaMs(liveLoaded + e.bytesFailed, s.bytesTotal);
                s.progress = s.bytesTotal ? Math.min(1, (liveLoaded + e.bytesFailed) / s.bytesTotal) : 0;
                s.activeTransfers = activeArr;
                s.elapsedMs = now - s.startTime;
                s.samples = pushSample(s.samples, rate, SAMPLE_CAP);
            } else if (s.phase === "finalizing") {
                anyActive = true;
                // processed only advances on a poll return; only feed the tracker
                // on a real change so idle ticks don't drag the rate to zero.
                if (s.processed !== e._lastProcessed) {
                    e.procTracker.update(s.processed, now);
                    e._lastProcessed = s.processed;
                }
                const rate = e.procTracker.rate;
                s.speed = rate;
                s.rateUnit = "files";
                const total = s.processedTotal || s.filesTotal;
                s.etaMs = e.procTracker.etaMs(s.processed, total);
                s.progress = total ? Math.min(1, s.processed / total) : 0;
                s.elapsedMs = now - s.startTime;
                s.samples = pushSample(s.samples, rate, SAMPLE_CAP);
            }
        }
        flush();
        if (!anyActive) {
            clearInterval(samplerRef.current);
            samplerRef.current = null;
        }
    }, [flush]);

    const ensureSampler = useCallback(() => {
        if (samplerRef.current == null) {
            samplerRef.current = setInterval(tick, SAMPLE_MS);
        }
    }, [tick]);

    useEffect(() => () => clearInterval(samplerRef.current), []);

    const filesChangedSoon = useCallback(() => {
        // Decoupled refresh signal — FileBrowser (wherever mounted) listens and
        // refetches. Robust to the launching modal having since unmounted.
        try {
            window.dispatchEvent(new CustomEvent("qdex:files-changed"));
        } catch {
            /* non-browser env (tests) */
        }
    }, []);

    // ── session bookkeeping ───────────────────────────────────────────────────
    const newSession = useCallback((partial) => {
        const id = (crypto.randomUUID && crypto.randomUUID()) || `up-${Date.now()}-${Math.round(performance.now())}`;
        const session = {
            id,
            kind: "import",
            title: "Upload",
            sourceName: null, // imported folder / .zip name (null for loose files)
            phase: "uploading",
            createdAt: Date.now(),
            startTime: Date.now(),
            filesTotal: 0,
            filesDone: 0,
            filesUploaded: 0,
            filesFailed: 0,
            bytesTotal: 0,
            bytesLoaded: 0,
            bytesFailed: 0,
            speed: 0,
            rateUnit: "bytes",
            etaMs: Infinity,
            elapsedMs: 0,
            progress: 0,
            samples: [],
            activeTransfers: [],
            processed: 0,
            processedTotal: 0,
            succeededLive: 0,
            failedLive: 0,
            warnings: [],
            result: { succeeded: 0, failed: 0 },
            failures: [],
            uploadedFiles: [],
            canCancel: true,
            ...partial,
        };
        sessionsRef.current.set(id, session);
        return session;
    }, []);

    const patch = useCallback((id, p) => {
        const s = sessionsRef.current.get(id);
        if (s) Object.assign(s, typeof p === "function" ? p(s) : p);
    }, []);

    // ── IMPORT engine ─────────────────────────────────────────────────────────
    const finalizeRun = useCallback(
        (id) => {
            const s = sessionsRef.current.get(id);
            const e = enginesRef.current.get(id);
            if (!s || !e) return;

            const jobDocs = Object.values(e.jobsById);
            const keyToJob = {};
            e.runJobs.forEach(({ jobId, clientKeys }) => clientKeys.forEach((k) => (keyToJob[k] = jobId)));
            const crashedJobIds = new Set(jobDocs.filter((j) => j.status === "failed").map((j) => j.id));
            // Per-file failures from a job that COMPLETED. The seam rolls back on a
            // per-file failure, so these files were never registered.
            const doneJobFailedKeys = new Set();
            jobDocs.forEach((j) => {
                if (j.status === "failed") return;
                (j.results || []).filter((r) => r.status === "failed").forEach((r) => doneJobFailedKeys.add(r.clientKey));
            });

            // Classify ONLY the records this run processed; leave others untouched.
            //
            // EVERY failure here is SAFE to retry, because retry can't duplicate:
            //  • an UPLOAD that failed never reached GCS → it's re-uploaded under a
            //    fresh fileId (nothing to collide with);
            //  • anything that DID upload keeps its fileId + gcsDestinationPath and
            //    is re-IMPORTED by that SAME fileId. The backend finalize is
            //    idempotent on fileId (createFileRecordFromGcsObject does a Firestore
            //    create(); a file a crashed/duplicate run already registered surfaces
            //    FILE_ALREADY_EXISTS, which finalizeFileWithRetry treats as success),
            //    so re-importing one that already landed is a no-op — never a copy.
            // That's why a crashed job's UNKNOWN per-file outcomes are no longer a
            // dead-end: we re-import the whole chunk and let the backend dedupe.
            e.runList.forEach((r) => {
                const jobId = keyToJob[r.clientKey];
                if (r._status === "uploadFailed") {
                    r._status = "failed";
                    r._retryable = true; // never reached GCS → re-upload
                    r._error = r._uploadError || "Upload failed.";
                } else if (r._createError) {
                    r._status = "failed";
                    r._retryable = true; // uploaded; re-import is idempotent on fileId
                    r._error = "Couldn’t confirm it was registered — retrying is safe.";
                } else if (jobId && crashedJobIds.has(jobId)) {
                    r._status = "failed";
                    r._retryable = true; // uploaded; re-import is idempotent on fileId
                    r._error = "The import was interrupted — retrying is safe.";
                } else if (jobId && doneJobFailedKeys.has(r.clientKey)) {
                    r._status = "failed";
                    r._retryable = true; // rolled back server-side → safe to re-import
                    r._error = "Couldn’t be registered — retrying is safe.";
                } else if (jobId) {
                    r._status = "done";
                } else {
                    r._status = "failed";
                    r._retryable = true; // uploaded but its chunk never created a job
                    r._error = "Couldn’t be registered — retrying is safe.";
                }
            });

            const failed = e.records.filter((r) => r._status === "failed");
            const succeeded = e.records.filter((r) => r._status === "done").length;

            s.failures = failed.map((r) => ({
                name: r.name,
                folderPath: r.folderPath,
                error: r._error,
                retryable: r._retryable,
            }));
            // The roster of files that actually landed (cumulative across retries),
            // for the "Import complete" summary.
            s.uploadedFiles = e.records
                .filter((r) => r._status === "done")
                .map((r) => ({ name: r.name, folderPath: r.folderPath }));
            s.result = { succeeded, failed: failed.length };
            s.retryableCount = failed.filter((r) => r._retryable).length;
            s.phase = "done";
            s.canCancel = false;
            s.etaMs = 0;
            s.progress = 1;
            s.activeTransfers = [];

            if (failed.length === 0 && e.workspaceId) clearSnapshot(e.workspaceId);
            filesChangedSoon();
            flush();
        },
        [filesChangedSoon, flush]
    );

    // Force a finalizing run to a terminal FAILED state when the server side can
    // no longer be tracked — a stalled (frozen-at-"processing") job or repeated
    // poll failures. Every job this run created that hasn't already settled is
    // marked CRASHED: an OOM-killed / evicted worker leaves the job doc frozen
    // with UNKNOWN per-file outcomes. finalizeRun then classifies those files as
    // failed-but-retryable — they're surfaced to the user AND included in "Retry
    // failed", because re-importing them by their original fileId is idempotent
    // on the backend (a file that actually landed collides → treated as success →
    // never duplicated). This reuses the exact correctness-critical classification
    // in finalizeRun rather than inventing a second one. The server-reported
    // succeeded count (s.succeededLive) is kept so the UI can still tell the user
    // roughly how many files made it before the interruption.
    const interruptRun = useCallback(
        (id, { title, message }) => {
            const e = enginesRef.current.get(id);
            const s = sessionsRef.current.get(id);
            if (!e || !s) return;
            e.pollStopped = true;
            clearTimeout(e.pollTimer);
            (e.runJobs || []).forEach(({ jobId }) => {
                const j = e.jobsById[jobId];
                if (!j || j.status === "processing") {
                    e.jobsById[jobId] = { ...(j || {}), id: jobId, status: "failed" };
                }
            });
            finalizeRun(id);
            patch(id, { phase: "error", canCancel: false });
            notify({ type: "error", title, message });
            flush();
        },
        [finalizeRun, patch, flush]
    );

    const pollJobs = useCallback(
        (id, jobIds) => {
            const e = enginesRef.current.get(id);
            if (!e) return;
            let consecutiveFails = 0;
            e.pollStopped = false;
            e._lastProgressAt = Date.now();
            e._stallProcessed = -1;
            const tickPoll = async () => {
                if (e.canceled || e.pollStopped) return;
                let anyProcessing = false;
                let tickHadError = false;
                await Promise.all(
                    jobIds.map(async (jid) => {
                        const cur = e.jobsById[jid];
                        if (cur && cur.status !== "processing") return; // already settled
                        try {
                            const j = await ImportService.getImportStatus(jid, SILENT);
                            e.jobsById[jid] = j;
                            if (j.status === "processing") anyProcessing = true;
                        } catch {
                            tickHadError = true;
                            anyProcessing = true; // unknown → keep waiting (bounded below)
                        }
                    })
                );
                if (e.canceled || e.pollStopped) return;

                // Aggregate the finalizing progress for the panel.
                const docs = Object.values(e.jobsById);
                const s = sessionsRef.current.get(id);
                if (s) {
                    s.processed = docs.reduce((sum, j) => sum + (j.processed || 0), 0);
                    s.processedTotal = docs.reduce((sum, j) => sum + (j.total || 0), 0) || e.uploadedCount;
                    s.succeededLive = docs.reduce((sum, j) => sum + (j.succeeded || 0), 0);
                    s.failedLive = docs.reduce((sum, j) => sum + (j.failed || 0), 0);
                    s.warnings = [...e.runWarnings, ...docs.flatMap((j) => j.warnings || [])];
                }

                if (!anyProcessing) {
                    finalizeRun(id);
                    return;
                }

                // Stall watchdog. The server is still answering polls but the job's
                // processed count hasn't advanced for STALL_TIMEOUT_MS — its worker
                // almost certainly died (e.g. OOM-killed, which leaves the doc frozen
                // at "processing" because the kill skips the worker's own .catch).
                // The poll-error path below never trips here (the GET keeps
                // succeeding), so detect the stall explicitly instead of polling a
                // dead job forever.
                const now = Date.now();
                if (s && s.processed !== e._stallProcessed) {
                    e._stallProcessed = s.processed;
                    e._lastProgressAt = now;
                }
                if (now - e._lastProgressAt > STALL_TIMEOUT_MS) {
                    interruptRun(id, {
                        title: "Import stopped responding",
                        message:
                            "The import stopped making progress and was halted — the server may have run out of memory. " +
                            "Use “Retry failed” to finish the rest; files that already imported are skipped automatically.",
                    });
                    return;
                }

                consecutiveFails = tickHadError ? consecutiveFails + 1 : 0;
                if (consecutiveFails >= MAX_POLL_FAILS) {
                    interruptRun(id, {
                        title: "Lost contact with the import",
                        message:
                            "The import's progress can no longer be tracked. Files that finished importing are saved; " +
                            "use “Retry failed” to import the rest — already-imported files are skipped automatically.",
                    });
                    return;
                }
                e.pollTimer = setTimeout(tickPoll, POLL_MS);
            };
            tickPoll();
        },
        [finalizeRun, interruptRun]
    );

    const executeImportRun = useCallback(
        async (id, records, { suppressGroup = false } = {}) => {
            const e = enginesRef.current.get(id);
            const s = sessionsRef.current.get(id);
            if (!e || !s) return;

            e.runList = records;
            e.jobsById = {};
            e.runJobs = [];
            e.active = new Map();
            e.bytesCompleted = 0;
            e.bytesFailed = 0;
            e.filesUploaded = 0;
            e.filesFailed = 0;
            e.rateTracker = createRateTracker();
            records.forEach((r) => {
                r._uploadError = null;
                r._createError = null;
                r._retryable = false;
                r._error = null;
                if (r.fileId && r.gcsDestinationPath) {
                    // Bytes already reached GCS on a prior run (e.g. a crashed or
                    // lost import): SKIP the re-upload and re-import by the SAME
                    // fileId. The backend finalize is idempotent on fileId, so a
                    // file a previous run already registered collides → treated as
                    // success → never duplicated.
                    r._status = "uploaded";
                } else {
                    r._status = "pending";
                    r.fileId = null;
                    r.gcsDestinationPath = null;
                }
            });

            Object.assign(s, {
                phase: "uploading",
                startTime: Date.now(),
                filesTotal: records.length,
                filesDone: 0,
                filesUploaded: 0,
                filesFailed: 0,
                // Already-uploaded records transfer no bytes (re-import only), so
                // they don't count toward the upload progress total.
                bytesTotal: records.reduce((sum, r) => sum + (r._status === "uploaded" ? 0 : r.file?.size || 0), 0),
                bytesLoaded: 0,
                bytesFailed: 0,
                samples: [],
                activeTransfers: [],
                warnings: [],
                progress: 0,
                etaMs: Infinity,
                canCancel: true,
            });
            ensureSampler();
            flush();

            // 1. Upload bytes in waves (URLs minted per wave; each has a bounded TTL).
            //    Records whose bytes are already in GCS (a retry of a previously-
            //    uploaded file) are already "uploaded" and skip straight to import.
            const toUpload = records.filter((r) => r._status !== "uploaded");
            for (let start = 0; start < toUpload.length; start += WAVE_SIZE) {
                if (e.canceled) break;
                const wave = toUpload.slice(start, start + WAVE_SIZE);
                let minted;
                try {
                    minted = await ImportService.batchInitUpload(
                        wave.map((r) => ({
                            clientKey: r.clientKey,
                            originalname: r.name,
                            mimetype: r.file.type || "application/octet-stream",
                            size: r.file.size,
                        })),
                        SILENT
                    );
                } catch (err) {
                    wave.forEach((r) => {
                        r._uploadError = err?.response?.data?.message || "Could not start upload.";
                        r._status = "uploadFailed";
                        e.bytesFailed += r.file?.size || 0;
                        e.filesFailed += 1;
                    });
                    continue; // other waves may still succeed; these retry later
                }
                const byKey = {};
                minted.forEach((m) => (byKey[m.clientKey] = m));

                await runPool(wave, UPLOAD_CONCURRENCY, async (r) => {
                    if (e.canceled) return;
                    const m = byKey[r.clientKey];
                    const tr = { key: r.clientKey, name: r.name, size: r.file.size, loaded: 0 };
                    e.active.set(r.clientKey, tr);
                    try {
                        if (!m) throw new Error("No upload URL was issued for this file.");
                        await ImportService.uploadToSignedUrl(
                            m,
                            r.file,
                            (loaded) => {
                                tr.loaded = loaded;
                            },
                            e.abort.signal
                        );
                        r.fileId = m.fileId;
                        r.gcsDestinationPath = m.gcsDestinationPath;
                        r._status = "uploaded";
                        e.bytesCompleted += r.file.size;
                        e.filesUploaded += 1;
                    } catch (err) {
                        if (e.canceled) {
                            r._status = "pending"; // cancelled mid-flight → not counted as failure
                        } else {
                            r._uploadError = "Upload to storage failed.";
                            r._status = "uploadFailed";
                            e.bytesFailed += r.file?.size || 0;
                            e.filesFailed += 1;
                        }
                    } finally {
                        e.active.delete(r.clientKey);
                    }
                });
            }

            if (e.canceled) {
                Object.assign(s, { phase: "canceled", canCancel: false, activeTransfers: [], etaMs: 0 });
                flush();
                return;
            }

            const uploaded = records.filter((r) => r._status === "uploaded");
            e.uploadedCount = uploaded.length;
            if (uploaded.length === 0) {
                finalizeRun(id); // nothing uploaded — classify & surface
                patch(id, { phase: "error" });
                flush();
                return;
            }

            // 2. Register in chunks of ≤ CREATE_CHUNK (backend caps each job at 1000).
            Object.assign(s, { phase: "finalizing", processed: 0, processedTotal: uploaded.length, samples: [] });
            e.procTracker = createRateTracker();
            e._lastProcessed = 0;
            flush();

            const chunks = [];
            for (let c = 0; c < uploaded.length; c += CREATE_CHUNK) chunks.push(uploaded.slice(c, c + CREATE_CHUNK));
            const multiChunk = chunks.length > 1;
            const warnings = [];
            const wantGroup = e.createGroup && !suppressGroup;
            if (multiChunk && wantGroup) {
                warnings.push(`Collection was not created — imports over ${CREATE_CHUNK} files are split into multiple jobs.`);
            }
            e.runWarnings = warnings;

            const jobIds = [];
            for (const chunk of chunks) {
                try {
                    const { jobId } = await ImportService.createImport(
                        {
                            createGroup: wantGroup && !multiChunk,
                            groupName: e.groupName?.trim() || undefined,
                            files: chunk.map((r) => ({
                                clientKey: r.clientKey,
                                fileId: r.fileId,
                                gcsDestinationPath: r.gcsDestinationPath,
                                originalname: r.name,
                                mimetype: r.file.type || "application/octet-stream",
                                folderPath: r.folderPath || undefined,
                                tags: r.tags,
                                metadata: r.metadata,
                            })),
                        },
                        SILENT
                    );
                    jobIds.push(jobId);
                    e.runJobs.push({ jobId, clientKeys: chunk.map((r) => r.clientKey) });
                } catch (err) {
                    chunk.forEach((r) => (r._createError = "Could not register these files."));
                    reportError(err, { title: "Part of the import could not be registered" });
                }
            }

            if (jobIds.length === 0) {
                finalizeRun(id); // everything failed to register
                patch(id, { phase: "error" });
                flush();
                return;
            }
            // Once jobs exist the server owns the work — cancel can no longer help.
            patch(id, { canCancel: false });
            pollJobs(id, jobIds);
        },
        [ensureSampler, flush, finalizeRun, patch, pollJobs]
    );

    const startImport = useCallback(
        ({ records, createGroup, groupName, workspaceId, title, sourceName }) => {
            const session = newSession({
                kind: "import",
                title: title || `Importing ${records.length} file${records.length === 1 ? "" : "s"}`,
                sourceName: sourceName || null,
                filesTotal: records.length,
                bytesTotal: records.reduce((sum, r) => sum + (r.file?.size || 0), 0),
            });
            enginesRef.current.set(session.id, {
                kind: "import",
                canceled: false,
                abort: new AbortController(),
                records, // cumulative session list
                runList: [],
                jobsById: {},
                runJobs: [],
                runWarnings: [],
                active: new Map(),
                bytesCompleted: 0,
                bytesFailed: 0,
                filesUploaded: 0,
                filesFailed: 0,
                uploadedCount: 0,
                rateTracker: createRateTracker(),
                procTracker: createRateTracker(),
                _lastProcessed: 0,
                createGroup,
                groupName,
                workspaceId,
            });
            flush();
            executeImportRun(session.id, records);
            return session.id;
        },
        [newSession, flush, executeImportRun]
    );

    // ── QUICK upload engine ──────────────────────────────────────────────────
    const executeQuickRun = useCallback(
        async (id, records) => {
            const e = enginesRef.current.get(id);
            const s = sessionsRef.current.get(id);
            if (!e || !s) return;

            e.active = new Map();
            e.bytesCompleted = 0;
            e.bytesFailed = 0;
            e.filesUploaded = 0;
            e.filesFailed = 0;
            e.rateTracker = createRateTracker();
            records.forEach((r) => {
                r._status = "pending";
                r._error = null;
            });

            Object.assign(s, {
                phase: "uploading",
                startTime: Date.now(),
                filesTotal: records.length,
                filesDone: 0,
                bytesTotal: records.reduce((sum, r) => sum + (r.file?.size || 0), 0),
                bytesLoaded: 0,
                bytesFailed: 0,
                samples: [],
                activeTransfers: [],
                progress: 0,
                etaMs: Infinity,
                canCancel: true,
            });
            ensureSampler();
            flush();

            await runPool(records, QUICK_CONCURRENCY, async (r) => {
                if (e.canceled) return;
                const tr = { key: r.clientKey, name: r.name, size: r.file.size, loaded: 0 };
                e.active.set(r.clientKey, tr);
                try {
                    const result = await FileService.uploadFile(
                        r.file,
                        e.transcribe,
                        e.metadata,
                        e.tags,
                        (loaded) => {
                            tr.loaded = loaded;
                        },
                        { ...SILENT, signal: e.abort.signal }
                    );
                    r._status = "done";
                    r.uploadedId = result?.id;
                    e.bytesCompleted += r.file.size;
                    e.filesUploaded += 1;
                    const isAudio = (r.file.type || "").startsWith("audio");
                    // Reassign (not push) so the flush snapshot gets a fresh array
                    // identity without per-tick cloning.
                    s.uploadedFiles = [...s.uploadedFiles, { name: r.name, fileId: result?.id, isAudio }];
                } catch (err) {
                    if (e.canceled) {
                        r._status = "pending";
                    } else {
                        r._status = "failed";
                        r._retryable = true; // never finalized → always safe to retry
                        // These requests opt out of the global error toast
                        // (SILENT), so this string is the ONLY thing the user
                        // ever sees. A bare "Upload failed." hid every quota,
                        // 413 and rejected-mimetype reason the server sent —
                        // reasons the import engine already surfaces (see the
                        // _uploadError above).
                        r._error =
                            err?.response?.data?.message ||
                            err?.message ||
                            "Upload failed.";
                        e.bytesFailed += r.file?.size || 0;
                        e.filesFailed += 1;
                    }
                } finally {
                    e.active.delete(r.clientKey);
                }
            });

            if (e.canceled) {
                Object.assign(s, { phase: "canceled", canCancel: false, activeTransfers: [], etaMs: 0 });
                flush();
                return;
            }

            const failed = e.records.filter((r) => r._status === "failed");
            const succeeded = e.records.filter((r) => r._status === "done").length;
            Object.assign(s, {
                phase: succeeded === 0 && failed.length > 0 ? "error" : "done",
                canCancel: false,
                etaMs: 0,
                progress: 1,
                activeTransfers: [],
                result: { succeeded, failed: failed.length },
                retryableCount: failed.length,
                failures: failed.map((r) => ({ name: r.name, error: r._error, retryable: true })),
            });
            if (succeeded > 0) filesChangedSoon();
            flush();
        },
        [ensureSampler, flush, filesChangedSoon]
    );

    const startQuickUpload = useCallback(
        ({ files, transcribe, metadata, tags, title }) => {
            const records = files.map((f) => ({
                clientKey: (crypto.randomUUID && crypto.randomUUID()) || `q-${Date.now()}-${Math.random()}`,
                file: f,
                name: f.name,
            }));
            const session = newSession({
                kind: "quick",
                title: title || `Uploading ${records.length} file${records.length === 1 ? "" : "s"}`,
                filesTotal: records.length,
                bytesTotal: records.reduce((sum, r) => sum + (r.file?.size || 0), 0),
            });
            enginesRef.current.set(session.id, {
                kind: "quick",
                canceled: false,
                abort: new AbortController(),
                records,
                active: new Map(),
                bytesCompleted: 0,
                bytesFailed: 0,
                filesUploaded: 0,
                filesFailed: 0,
                rateTracker: createRateTracker(),
                transcribe,
                metadata,
                tags,
            });
            flush();
            executeQuickRun(session.id, records);
            return session.id;
        },
        [newSession, flush, executeQuickRun]
    );

    // ── retry / cancel / dismiss ──────────────────────────────────────────────
    const retrySession = useCallback(
        (id) => {
            const e = enginesRef.current.get(id);
            const s = sessionsRef.current.get(id);
            if (!e || !s) return;
            // Every failed record is retryable: an upload that never reached GCS is
            // re-uploaded; anything already in GCS is re-imported by its original
            // fileId (the backend dedupes on fileId, so it can't duplicate).
            const retryRecords = e.records.filter((r) => r._status === "failed" && r._retryable);
            if (retryRecords.length === 0) {
                notify({ type: "info", title: "Nothing to retry", message: "All files imported successfully." });
                return;
            }
            retryRecords.forEach((r) => {
                r.clientKey = (crypto.randomUUID && crypto.randomUUID()) || `r-${Date.now()}-${Math.random()}`;
                r._status = "pending";
                r._retryable = false;
                r._uploadError = null;
                r._createError = null;
                r._error = null;
                // Do NOT clear fileId/gcsDestinationPath: when the upload already
                // succeeded, keeping them makes executeImportRun RE-IMPORT by that
                // same fileId (idempotent → no duplicate) instead of re-uploading.
                // Records whose upload failed have neither, so they re-upload.
            });
            e.canceled = false;
            e.abort = new AbortController();
            if (e.kind === "import") executeImportRun(id, retryRecords, { suppressGroup: true });
            else executeQuickRun(id, retryRecords);
        },
        [executeImportRun, executeQuickRun]
    );

    const cancelSession = useCallback(
        (id) => {
            const e = enginesRef.current.get(id);
            const s = sessionsRef.current.get(id);
            if (!e || !s) return;
            e.canceled = true;
            try {
                e.abort.abort();
            } catch {
                /* already aborted */
            }
            clearTimeout(e.pollTimer);
            // The upload/quick loop notices `canceled` and settles the phase; nudge
            // the UI immediately so the card reflects the cancel without waiting.
            if (isActivePhase(s.phase)) patch(id, { canCancel: false });
            flush();
        },
        [patch, flush]
    );

    const dismissSession = useCallback(
        (id) => {
            const e = enginesRef.current.get(id);
            if (e) {
                e.canceled = true;
                try {
                    e.abort.abort();
                } catch {
                    /* noop */
                }
                clearTimeout(e.pollTimer);
            }
            sessionsRef.current.delete(id);
            enginesRef.current.delete(id);
            setPresentedIds((prev) => {
                if (!prev.has(id)) return prev;
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            flush();
        },
        [flush]
    );

    const clearFinished = useCallback(() => {
        for (const [id, s] of [...sessionsRef.current]) {
            if (!isActivePhase(s.phase)) {
                enginesRef.current.delete(id);
                sessionsRef.current.delete(id);
            }
        }
        flush();
    }, [flush]);

    // ── present / unpresent (a session shown in a modal is hidden from tray) ───
    const present = useCallback((id) => {
        setPresentedIds((prev) => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    }, []);
    const unpresent = useCallback((id) => {
        setPresentedIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const value = useMemo(
        () => ({
            sessions,
            presentedIds,
            trayOpen,
            setTrayOpen,
            startImport,
            startQuickUpload,
            retrySession,
            cancelSession,
            dismissSession,
            clearFinished,
            present,
            unpresent,
        }),
        [
            sessions,
            presentedIds,
            trayOpen,
            startImport,
            startQuickUpload,
            retrySession,
            cancelSession,
            dismissSession,
            clearFinished,
            present,
            unpresent,
        ]
    );

    return <UploadManagerContext.Provider value={value}>{children}</UploadManagerContext.Provider>;
}

/** Access the upload manager. Throws if used outside the provider. */
export function useUploadManager() {
    const ctx = useContext(UploadManagerContext);
    if (!ctx) throw new Error("useUploadManager must be used within an UploadManagerProvider");
    return ctx;
}

/** Subscribe to a single session snapshot (or null if it no longer exists). */
export function useUploadSession(id) {
    const { sessions } = useUploadManager();
    return useMemo(() => sessions.find((s) => s.id === id) || null, [sessions, id]);
}

export default UploadManagerContext;
